/**
 * AI Assistant Drawer Component
 *
 * Main orchestrator for the AI Assistant feature.
 */

import { AIAssistantState } from './state/ai-assistant-state.js';
import { AIAssistantService } from '../../services/domain/ai-assistant-service.js';
import { createConversationSidebar } from './conversation-sidebar.js';
import { createChatInput } from './chat-input.js';
import {
  createMessageBubble,
  updateAssistantMessageBubble,
  hasAssistantTextContent,
  resolveUserDisplayContent,
} from './message-bubble.js';
import { createSavedPromptsModal } from './saved-prompts-modal.js';
import { createClarificationSheetUi } from './clarification-sheet-ui.js';
import { deriveClarificationSheet } from './rendering/clarification-sheet.js';
import { createPinnedRecordBar } from './pinned-record-bar.js';
import {
  canShowMore,
  findLiveProposal,
  findNewestEntityList,
  listPageSize,
  pinAfterNewestList,
  pinFromRow,
  reduceListSelection,
  selectionBlockKey,
  turnForCancel,
  turnForComposerSend,
  turnForConfirm,
  turnForPageAction,
  turnForPlainMessage,
  turnForSummarizeRecord,
} from './list-selection.js';
import { createConversationFeedback } from './conversation-feedback.js';
import { createAssistantMark } from './utils/assistant-mark.js';
import { Icons } from './utils/icons.js';
import { Logger } from '../../services/utils/logger.js';
import { t } from '../../i18n.js';
import {
  isAiCreditLimitReached,
  isCreditLimitMessage,
  openUsageLimitModal,
} from '../../services/domain/ai-credits.js';

/**
 * Create the AI Assistant drawer
 * @param {Object} options
 * @param {Function} options.onClose - Callback when drawer is closed
 * @returns {HTMLElement}
 */
export function createAIAssistantDrawer({ onClose }) {
  const state = new AIAssistantState();

  const drawer = document.createElement('div');
  drawer.className = 'ai-assistant-drawer';
  drawer.id = 'ai-assistant-drawer';

  const overlay = document.createElement('div');
  overlay.className = 'ai-drawer-overlay';
  overlay.onclick = () => {
    if (!isMinimized) handleClose();
  };

  const panel = document.createElement('div');
  panel.className = 'ai-drawer-panel';

  const minimizeTab = document.createElement('button');
  minimizeTab.type = 'button';
  minimizeTab.className = 'ai-drawer-minimize-tab';
  minimizeTab.setAttribute('aria-expanded', 'true');
  minimizeTab.setAttribute('aria-label', t('ai_minimize_title'));
  minimizeTab.innerHTML = `<span class="ai-drawer-minimize-tab__icon">${Icons.chevronRight()}</span>`;
  minimizeTab.onclick = (e) => {
    e.stopPropagation();
    toggleMinimized();
  };

  const body = document.createElement('div');
  body.className = 'ai-drawer-body';

  const sidebar = createConversationSidebar({
    state,
    onNewChat: handleNewChat,
    onSelectConversation: handleSelectConversation,
    onBookmark: handleToggleBookmark,
    onDelete: handleDeleteConversation,
    onClose: () => {
      state.setSidebarOpen(false);
      updateSidebarVisibility();
    },
  });

  const mainArea = document.createElement('div');
  mainArea.className = 'ai-main-area';

  const chatHeader = document.createElement('div');
  chatHeader.className = 'ai-chat-header';

  const toggleSidebarBtn = document.createElement('button');
  toggleSidebarBtn.className = 'ai-drawer-icon-btn ai-toggle-sidebar-btn';
  toggleSidebarBtn.innerHTML = Icons.menu();
  toggleSidebarBtn.title = t('ai_toggle_sidebar_title');
  toggleSidebarBtn.onclick = handleToggleSidebar;

  const brand = document.createElement('div');
  brand.className = 'ai-chat-header-brand';
  brand.appendChild(createAssistantMark({ size: 18, className: 'ai-mark' }));

  const chatTitle = document.createElement('h3');
  chatTitle.className = 'ai-chat-title';
  chatTitle.id = 'ai-chat-title';
  chatTitle.textContent = t('ai_assistant_label');
  brand.appendChild(chatTitle);

  const headerActions = document.createElement('div');
  headerActions.className = 'ai-chat-header-actions';

  const minimizeBtn = document.createElement('button');
  minimizeBtn.className = 'ai-drawer-icon-btn';
  minimizeBtn.innerHTML = Icons.chevronRight();
  minimizeBtn.title = t('ai_minimize_title');
  minimizeBtn.setAttribute('aria-label', t('ai_minimize_title'));
  minimizeBtn.onclick = () => setMinimized(true);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'ai-drawer-icon-btn';
  closeBtn.innerHTML = Icons.close();
  closeBtn.title = t('ai_close_title');
  closeBtn.setAttribute('aria-label', t('ai_close_title'));
  closeBtn.onclick = handleClose;

  headerActions.append(minimizeBtn, closeBtn);
  chatHeader.append(toggleSidebarBtn, brand, headerActions);

  const messagesContainer = document.createElement('div');
  messagesContainer.className = 'ai-messages-container';
  messagesContainer.id = 'ai-messages-container';
  messagesContainer.appendChild(createWelcomeMessage());

  const composerDock = document.createElement('div');
  composerDock.className = 'ai-composer-dock';

  const sheetSlot = document.createElement('div');
  sheetSlot.className = 'ai-sheet-slot';

  const pinSlot = document.createElement('div');
  pinSlot.className = 'ai-pin-slot';

  const conversationFeedback = createConversationFeedback({
    rating: null,
    disabled: true,
    onSubmit: handleConversationFeedback,
  });
  conversationFeedback.hidden = true;

  const chatInput = createChatInput({
    onSend: handleSendMessage,
    onOpenPrompts: handleOpenPrompts,
    onEscape: handleComposerEscape,
    disabled: false,
  });

  composerDock.append(sheetSlot, conversationFeedback, pinSlot, chatInput);

  mainArea.append(chatHeader, messagesContainer, composerDock);
  body.append(sidebar, mainArea);
  panel.append(minimizeTab, body);
  drawer.append(overlay, panel);

  let streamRenderRafId = null;
  let isDrawerClosing = false;
  let isMinimized = false;
  let keyHandler = null;
  let userIsNearBottom = true;
  const messageFeedbackMap = {};
  let conversationRating = null;
  let sheetUi = null;
  let sheetDismissedId = null;

  messagesContainer.addEventListener('scroll', () => {
    const distanceFromBottom =
      messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight;
    userIsNearBottom = distanceFromBottom < 100;
  });

  function getRenderContextForMessage(message, messages, inFlightId) {
    const streaming = state.getStreaming();
    const isStreaming = message._id === inFlightId && streaming;
    return {
      isStreaming,
      showThinking: isStreaming && !hasAssistantTextContent(message),
      allMessages: messages,
      onChoiceSelect: handleChoiceSelect,
      onRetry: handleRetry,
      onFeedback: handleMessageFeedback,
      feedbackRating: messageFeedbackMap[message._id] ?? null,
      listSelection: {
        streaming,
        newest: findNewestEntityList(messages),
        liveProposal: findLiveProposal(messages, { streaming }),
        pin: state.getPin(),
        exhaustedListIds: state.getExhaustedListIds(),
        onPinRow: handlePinRow,
        onClearPin: handleClearPin,
        onShowMore: handleShowMore,
        onSummarizeList: handleSummarizeList,
        onConfirm: handleConfirmUpdate,
        onCancel: handleCancelUpdate,
      },
    };
  }

  function cancelStreamRender() {
    if (streamRenderRafId) {
      cancelAnimationFrame(streamRenderRafId);
      streamRenderRafId = null;
    }
  }

  function updateComposer() {
    const pin = state.getPin();
    if (state.getCommenting() && pin) {
      chatInput.setPlaceholder(t('ai_comment_placeholder', { label: pin.label || '' }));
      chatInput.setHint(t('ai_hint_comment'));
      return;
    }
    chatInput.setPlaceholder(pin ? t('ai_messages_apply_to', { n: pin.index }) : t('ai_chat_placeholder'));
    chatInput.setHint(pin ? t('ai_hint_pinned', { n: pin.index }) : t('ai_chat_hint'));
  }

  function updatePinBar() {
    const pin = state.getPin();
    if (!pin) {
      pinSlot.replaceChildren();
      return;
    }
    pinSlot.replaceChildren(
      createPinnedRecordBar({
        pin,
        commenting: state.getCommenting(),
        actionsDisabled: state.getStreaming(),
        onComment: handleStartComment,
        onSummarize: handleSummarizeRecord,
        onClear: handleClearPin,
      })
    );
  }

  function syncPinToNewestList() {
    const before = state.getPin();
    const next = pinAfterNewestList(before, state.getMessages());
    if (next === before) return false;
    state.setPin(next);
    if (!next) state.setCommenting(false);
    return true;
  }

  function applyCompletedTurn(turn, metadata) {
    const next = reduceListSelection({
      pin: state.getPin(),
      exhaustedListIds: state.getExhaustedListIds(),
      turn,
      messages: state.getMessages(),
      metadata,
    });
    state.setPin(next.pin);
    state.setExhaustedListIds(next.exhaustedListIds);
    if (!next.pin) state.setCommenting(false);
  }

  function handleComposerEscape() {
    if (!state.getCommenting()) return false;
    state.setCommenting(false);
    updateComposer();
    return true;
  }

  function handleClearPin() {
    state.setPin(null);
    state.setCommenting(false);
    updateComposer();
    renderMessages();
  }

  function handlePinRow(block, row) {
    if (state.getStreaming()) return;
    state.setPin(pinFromRow(block, row));
    state.setCommenting(false);
    updateComposer();
    renderMessages();
  }

  function handleStartComment() {
    const pin = state.getPin();
    if (!pin || pin.canComment === false || state.getStreaming()) return;
    state.setCommenting(!state.getCommenting());
    updateComposer();
    updatePinBar();
    if (state.getCommenting()) chatInput.focus();
  }

  function handleSummarizeRecord() {
    const pin = state.getPin();
    if (!pin || state.getStreaming()) return;
    const message = t('ai_summarize');
    void handleSendMessage(message, { turn: turnForSummarizeRecord(pin, message) });
  }

  function handleShowMore(block) {
    if (state.getStreaming() || !canShowMore(block, state.getExhaustedListIds())) return;
    const message = t('ai_show_more', { n: listPageSize(block) });
    void handleSendMessage(message, { turn: turnForPageAction('next_page', block, message) });
  }

  function handleSummarizeList(block) {
    if (state.getStreaming()) return;
    const message = t('ai_summarize_these', { n: listPageSize(block) });
    void handleSendMessage(message, { turn: turnForPageAction('summarize_list', block, message) });
  }

  function handleConfirmUpdate(block) {
    if (state.getStreaming()) return;
    const message = t('ai_confirm');
    void handleSendMessage(message, { turn: turnForConfirm(block, message) });
  }

  function handleCancelUpdate(block) {
    if (state.getStreaming()) return;
    const message = t('ai_cancel');
    void handleSendMessage(message, { turn: turnForCancel(block, message) });
  }

  function scheduleInFlightBubbleUpdate(inFlightId) {
    if (streamRenderRafId) cancelAnimationFrame(streamRenderRafId);
    streamRenderRafId = requestAnimationFrame(() => {
      streamRenderRafId = null;
      const message = state.getMessages().find((m) => m._id === inFlightId);
      if (!message) return;

      const ctx = getRenderContextForMessage(message, state.getMessages(), inFlightId);
      const updated = updateAssistantMessageBubble(inFlightId, message, ctx, messagesContainer);
      if (!updated) renderMessages();
      updateClarificationSheet();
      scrollToBottom();
    });
  }

  function setMinimized(next) {
    isMinimized = next;
    drawer.classList.toggle('ai-assistant-drawer--minimized', next);
    minimizeTab.setAttribute('aria-expanded', next ? 'false' : 'true');
    minimizeTab.setAttribute('aria-label', next ? t('ai_expand_title') : t('ai_minimize_title'));
    updateMinimizePulse();
  }

  function toggleMinimized() {
    setMinimized(!isMinimized);
  }

  function updateMinimizePulse() {
    const existing = minimizeTab.querySelector('.ai-drawer-minimize-tab__pulse');
    const show = isMinimized && state.getStreaming();
    if (show && !existing) {
      const pulse = document.createElement('span');
      pulse.className = 'ai-drawer-minimize-tab__pulse';
      pulse.setAttribute('aria-hidden', 'true');
      minimizeTab.appendChild(pulse);
    } else if (!show && existing) {
      existing.remove();
    }
  }

  function handleClose() {
    if (isDrawerClosing) return;
    isDrawerClosing = true;

    state.cancelCurrentRequest();
    if (streamRenderRafId) {
      cancelAnimationFrame(streamRenderRafId);
      streamRenderRafId = null;
    }

    const inFlightId = state.getInFlightAssistantMessageId();
    if (inFlightId) state.removeMessage(inFlightId);

    state.setStreaming(false);
    state.setInFlightAssistantMessageId(null);
    chatInput.setStreaming(false);

    if (keyHandler) {
      document.removeEventListener('keydown', keyHandler);
      keyHandler = null;
    }

    drawer.classList.add('closing');
    setTimeout(() => {
      drawer.remove();
      document.body.classList.remove('ai-drawer-open');
      if (onClose) onClose();
    }, 300);
  }

  function handleNewChat() {
    if (state.getStreaming()) return;
    Object.keys(messageFeedbackMap).forEach((k) => delete messageFeedbackMap[k]);
    conversationRating = null;
    sheetDismissedId = null;
    state.startNewChat();
    renderMessages();
    updateConversationFeedback();
    scrollToTop();
    chatInput.focus();
    sidebar.render();
    state.setSidebarOpen(false);
    updateSidebarVisibility();
  }

  function handleToggleSidebar() {
    state.toggleSidebar();
    updateSidebarVisibility();
  }

  function updateSidebarVisibility() {
    const isOpen = state.isSidebarVisible();
    sidebar.classList.toggle('collapsed', !isOpen);
    mainArea.classList.toggle('expanded', !isOpen);
  }

  async function hydrateMessageFeedback(messages) {
    const assistantIds = messages.filter((m) => m.role === 'assistant' && m._id).map((m) => m._id);
    await Promise.allSettled(
      assistantIds.map(async (id) => {
        try {
          const response = await AIAssistantService.getMessageFeedback(id);
          const rating = response?.feedback?.rating;
          if (rating === 1 || rating === -1) messageFeedbackMap[id] = rating;
        } catch {
          // Failures must not break chat
        }
      })
    );
  }

  async function hydrateConversationFeedback(conversationId) {
    conversationRating = null;
    if (!conversationId) {
      updateConversationFeedback();
      return;
    }
    try {
      const response = await AIAssistantService.getConversationFeedback(conversationId);
      conversationRating = response?.feedback?.rating ?? null;
    } catch {
      conversationRating = null;
    }
    updateConversationFeedback();
  }

  function updateConversationFeedback() {
    const conversationId = state.getCurrentConversationId();
    const show =
      !!conversationId && !state.getStreaming() && state.getMessages().length > 0;
    conversationFeedback.hidden = !show;
    conversationFeedback.update({
      rating: conversationRating,
      disabled: state.getStreaming(),
    });
  }

  async function handleSelectConversation(conversationId) {
    if (conversationId === state.getCurrentConversationId()) return;
    if (state.getStreaming()) return;

    Object.keys(messageFeedbackMap).forEach((k) => delete messageFeedbackMap[k]);
    sheetDismissedId = null;
    state.clearListSelection();
    updateComposer();
    updatePinBar();
    state.setCurrentConversation(conversationId);
    state.setLoadingMessages(true);

    showLoadingState();
    scrollToTop();

    try {
      const response = await AIAssistantService.getMessages(conversationId, { limit: 50 });
      const messages = response.messages || [];
      state.setMessages(messages, response.totalCount || messages.length);
      await Promise.all([
        hydrateMessageFeedback(messages),
        hydrateConversationFeedback(conversationId),
      ]);
      renderMessages();
      forceScrollToBottom();
      chatInput.focus();

      if (window.innerWidth < 768) {
        state.setSidebarOpen(false);
        updateSidebarVisibility();
      }
    } catch (error) {
      Logger.error('Failed to load messages', error);
    } finally {
      state.setLoadingMessages(false);
    }
  }

  function showLoadingState() {
    const container = document.getElementById('ai-messages-container');
    if (container) {
      container.innerHTML = `<div class="ai-loading-messages">${t('ai_loading_messages')}</div>`;
    }
  }

  async function handleToggleBookmark(conversationId, shouldBookmark) {
    try {
      if (shouldBookmark) {
        await AIAssistantService.bookmarkConversation(conversationId);
      } else {
        await AIAssistantService.unbookmarkConversation(conversationId);
      }

      state.updateConversation(conversationId, { isBookmarked: shouldBookmark });
      sidebar.render();
      loadBookmarkedConversations();
    } catch (error) {
      Logger.error('Failed to toggle bookmark', error);
    }
  }

  async function handleDeleteConversation(conversationId) {
    if (!confirm(t('ai_delete_confirm'))) return;

    try {
      await AIAssistantService.deleteConversation(conversationId);
      state.removeConversation(conversationId);
      conversationRating = null;
      renderMessages();
      updateConversationFeedback();
      sidebar.render();
    } catch (error) {
      Logger.error('Failed to delete conversation', error);
      alert(t('ai_delete_failed'));
    }
  }

  async function handleSendMessage(content, { isRetry = false, displayText, turn, plain = false } = {}) {
    const trimmed = content?.trim();
    if (!trimmed || state.getStreaming() || isDrawerClosing) return false;

    if (isAiCreditLimitReached()) {
      openUsageLimitModal();
      return false;
    }

    let requestTurn;
    if (isRetry) {
      requestTurn = state.getLastTurn();
      if (!requestTurn?.message) return false;
    } else if (plain) {
      requestTurn = turnForPlainMessage(trimmed);
    } else if (turn) {
      requestTurn = { ...turn, message: turn.message || trimmed };
    } else {
      requestTurn = turnForComposerSend({
        text: trimmed,
        pin: state.getPin(),
        commenting: state.getCommenting(),
      });
    }

    state.setLastTurn(requestTurn);
    if (!isRetry && state.getCommenting() && requestTurn.action === 'comment') {
      state.setCommenting(false);
    }

    let tempUserMessage = null;

    if (!isRetry) {
      tempUserMessage = state.addOptimisticMessage(
        requestTurn.message,
        displayText ?? requestTurn.message
      );
    }

    const inFlightId = AIAssistantState.makeLocalId('stream');

    state.addMessage({
      _id: inFlightId,
      role: 'assistant',
      content: '',
      parts: [],
      references: [],
      createdAt: new Date().toISOString(),
      isStreaming: true,
    });

    state.setInFlightAssistantMessageId(inFlightId);
    state.setStreaming(true);
    chatInput.setStreaming(true);
    sheetUi?.setDisabled?.(true);
    updateConversationFeedback();
    updateMinimizePulse();
    renderMessages();
    forceScrollToBottom();

    const controller = state.createAbortController();
    try {
      const result = await AIAssistantService.streamMessage(
        {
          conversationId: state.getCurrentConversationId(),
          message: requestTurn.message,
          listId: requestTurn.listId,
          focus: requestTurn.focus,
          action: requestTurn.action,
          proposalId: requestTurn.proposalId,
          onUpdate: (streamState) => {
            if (controller.signal.aborted || isDrawerClosing) return;
            const previous = state.getMessages().find((m) => m._id === inFlightId);
            const previousBlocks = selectionBlockKey(previous?.parts);
            state.updateMessage(inFlightId, {
              content: streamState.content,
              parts: streamState.parts,
              references: streamState.references,
            });
            const blocksChanged = previousBlocks !== selectionBlockKey(streamState.parts);
            const pinChanged = syncPinToNewestList();
            if (blocksChanged || pinChanged) {
              cancelStreamRender();
              renderMessages();
              scrollToBottom();
              return;
            }
            scheduleInFlightBubbleUpdate(inFlightId);
          },
          onError: (err) => {
            Logger.error('Stream error', err);
          },
        },
        controller.signal
      );

      if (!state.getCurrentConversationId() && result.conversationId) {
        await handleNewConversationCreated(result.conversationId);
      }

      if (tempUserMessage && result.userMessageId) {
        state.replaceOptimisticMessage(tempUserMessage._id, {
          _id: result.userMessageId,
          role: 'user',
          content: requestTurn.message,
          createdAt: new Date().toISOString(),
        });
      }

      let canonical = null;
      const shouldFetchCanonical =
        result.conversationId &&
        result.assistantMessageId &&
        (!result.parts?.length || !result.answer);

      if (shouldFetchCanonical) {
        try {
          canonical = await AIAssistantService.getMessage(
            result.conversationId,
            result.assistantMessageId
          );
        } catch (e) {
          Logger.error('Failed to fetch canonical message', e);
        }
      }

      const metadata = canonical?.metadata ?? result.metadata;

      state.updateMessage(inFlightId, {
        _id: result.assistantMessageId || inFlightId,
        role: 'assistant',
        content: canonical?.content ?? result.answer,
        parts: canonical?.parts ?? result.parts,
        references: canonical?.references ?? result.references,
        metadata,
        createdAt: canonical?.createdAt ?? new Date().toISOString(),
        isStreaming: false,
      });

      cancelStreamRender();
      state.setStreaming(false);
      state.setInFlightAssistantMessageId(null);
      applyCompletedTurn(requestTurn, metadata);

      renderMessages();
      scrollToBottom();
      sidebar.render();
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;

      Logger.error('Failed to send message', error);
      state.removeMessage(inFlightId);

      const streamError = error.streamError;
      const message =
        streamError?.message ||
        `${t('ai_error_prefix')} ${error.message}. ${t('ai_error_suffix')}`;

      if (isCreditLimitMessage(message)) {
        openUsageLimitModal();
      }

      cancelStreamRender();
      state.setStreaming(false);
      state.setInFlightAssistantMessageId(null);
      state.addMessage({
        _id: AIAssistantState.makeLocalId('error'),
        role: 'assistant',
        content: message,
        createdAt: new Date().toISOString(),
        isError: true,
        streamError,
      });

      renderMessages();
      return false;
    } finally {
      if (!isDrawerClosing) {
        state.setInFlightAssistantMessageId(null);
        state.setStreaming(false);
        chatInput.setStreaming(false);
        sheetUi?.setDisabled?.(false);
        updateConversationFeedback();
        updateMinimizePulse();
        updatePinBar();
      }
    }
  }

  async function handleRetry() {
    if (state.getStreaming()) return;
    const last = state.getLastTurn();
    if (!last?.message) return;

    const messages = state.getMessages();
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isError) {
        state.removeMessage(messages[i]._id);
        break;
      }
    }

    await handleSendMessage(last.message, { isRetry: true });
  }

  function handleChoiceSelect(value, label) {
    void handleSendMessage(value, { displayText: label ?? value, plain: true });
  }

  async function handleMessageFeedback(messageId, rating, note) {
    const conversationId = state.getCurrentConversationId();
    if (!conversationId) return;

    messageFeedbackMap[messageId] = rating;
    renderMessages();

    try {
      await AIAssistantService.submitMessageFeedback({
        conversationId,
        messageId,
        rating,
        ...(note ? { note } : {}),
      });
    } catch (error) {
      delete messageFeedbackMap[messageId];
      renderMessages();
      Logger.error('Failed to submit message feedback', error);
    }
  }

  async function handleConversationFeedback(rating, note) {
    const conversationId = state.getCurrentConversationId();
    if (!conversationId) return;

    conversationRating = rating;
    updateConversationFeedback();

    try {
      await AIAssistantService.submitConversationFeedback({
        conversationId,
        rating,
        ...(note ? { note } : {}),
      });
    } catch (error) {
      conversationRating = null;
      updateConversationFeedback();
      Logger.error('Failed to submit conversation feedback', error);
    }
  }

  async function handleNewConversationCreated(conversationId) {
    try {
      const conversation = await AIAssistantService.getConversation(conversationId);
      state.addConversation(conversation);
      state.setCurrentConversation(conversationId);
    } catch (error) {
      Logger.error('Failed to fetch new conversation', error);
      state.setCurrentConversation(conversationId);
    }
    updateConversationFeedback();
  }

  async function handleOpenPrompts() {
    try {
      const existingModal = document.querySelector('.ai-modal-overlay');
      if (existingModal) existingModal.remove();

      await loadSavedPrompts();

      const modal = createSavedPromptsModal({
        state,
        onSelectPrompt: (promptText) => {
          chatInput.setValue(promptText);
          chatInput.focus();
        },
        onClose: () => {
          modal.remove();
        },
      });

      document.body.appendChild(modal);
      requestAnimationFrame(() => {
        modal.classList.add('open');
      });
    } catch (error) {
      Logger.error('Failed to open saved prompts modal', error);
      alert(t('ai_prompts_failed'));
    }
  }

  function createWelcomeMessage() {
    const welcome = document.createElement('div');
    welcome.className = 'ai-welcome-message ai-grid-bg';
    welcome.id = 'ai-welcome-message';

    const hero = document.createElement('div');
    hero.className = 'ai-welcome-hero';
    hero.appendChild(createAssistantMark({ size: 20, className: 'ai-mark ai-mark--lg' }));

    const title = document.createElement('h3');
    title.textContent = t('ai_welcome_title');
    const bodyText = document.createElement('p');
    bodyText.textContent = t('ai_welcome_body');
    hero.append(title, bodyText);

    const suggestions = document.createElement('div');
    suggestions.className = 'ai-welcome-suggestions';

    [t('ai_suggestion_1'), t('ai_suggestion_2'), t('ai_suggestion_3')].forEach((text) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ai-suggestion-card';
      const label = document.createElement('span');
      label.className = 'ai-suggestion-card__label';
      label.textContent = text;
      const arrow = document.createElement('span');
      arrow.className = 'ai-suggestion-card__arrow';
      arrow.innerHTML = Icons.arrowRightOutlined();
      btn.append(label, arrow);
      btn.onclick = () => {
        void handleSendMessage(text);
      };
      suggestions.appendChild(btn);
    });

    welcome.append(hero, suggestions);
    return welcome;
  }

  function updateClarificationSheet() {
    const messages = state.getMessages();
    const sheet = deriveClarificationSheet(messages);
    const sheetId = sheet?.sheetId ?? null;

    if (!sheet || sheetId === sheetDismissedId) {
      sheetSlot.replaceChildren();
      sheetUi = null;
      return;
    }

    if (sheetUi && sheetUi.dataset.sheetId === sheetId) {
      sheetUi.setDisabled?.(state.getStreaming());
      return;
    }

    sheetUi = createClarificationSheetUi({
      sheet,
      disabled: state.getStreaming(),
      onSend: (message) => {
        void handleSendMessage(message, { plain: true });
      },
      onDismiss: () => {
        sheetDismissedId = sheetId;
        sheetSlot.replaceChildren();
        sheetUi = null;
        chatInput.focus();
      },
    });
    sheetUi.dataset.sheetId = sheetId;
    sheetSlot.replaceChildren(sheetUi);
  }

  function renderMessages() {
    const container = document.getElementById('ai-messages-container');
    if (!container) return;

    const scrollTop = container.scrollTop;
    const stickToBottom = userIsNearBottom;
    container.innerHTML = '';

    const messages = state.getMessages();
    if (messages.length === 0) {
      container.appendChild(createWelcomeMessage());
      updateClarificationSheet();
      updateConversationFeedback();
      updatePinBar();
      updateComposer();
      container.scrollTop = stickToBottom ? container.scrollHeight : scrollTop;
      return;
    }

    const inFlightId = state.getInFlightAssistantMessageId();

    messages.forEach((message, idx) => {
      container.appendChild(
        createMessageBubble(message, {
          ...getRenderContextForMessage(message, messages, inFlightId),
          displayContent: resolveUserDisplayContent(messages, idx),
        })
      );
    });

    updateClarificationSheet();
    updateConversationFeedback();
    updatePinBar();
    updateComposer();
    container.scrollTop = stickToBottom ? container.scrollHeight : scrollTop;
  }

  function scrollToBottom() {
    if (!userIsNearBottom) return;
    const container = document.getElementById('ai-messages-container');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  function forceScrollToBottom() {
    userIsNearBottom = true;
    scrollToBottom();
  }

  function scrollToTop() {
    const container = document.getElementById('ai-messages-container');
    if (container) {
      container.scrollTop = 0;
    }
  }

  async function loadConversations() {
    state.setLoadingConversations(true);
    try {
      const response = await AIAssistantService.getConversations({ limit: 50 });
      state.setConversations(response.conversations || [], response.totalCount || 0);
    } catch (error) {
      Logger.error('Failed to load conversations', error);
      state.setConversations([], 0);
    } finally {
      state.setLoadingConversations(false);
      sidebar.render();
    }
  }

  async function loadBookmarkedConversations() {
    try {
      const response = await AIAssistantService.getBookmarkedConversations({ limit: 20 });
      state.setBookmarkedConversations(response.conversations || [], response.totalCount || 0);
    } catch (error) {
      Logger.error('Failed to load bookmarked conversations', error);
      state.setBookmarkedConversations([], 0);
    } finally {
      sidebar.render();
    }
  }

  async function loadSavedPrompts() {
    try {
      const response = await AIAssistantService.getSavedPrompts();
      state.setSavedPrompts(response.prompts || [], response.totalCount || 0);
    } catch (error) {
      Logger.error('Failed to load saved prompts', error);
    }
  }

  loadConversations();
  loadBookmarkedConversations();
  updateSidebarVisibility();
  updateConversationFeedback();

  document.body.classList.add('ai-drawer-open');
  requestAnimationFrame(() => {
    drawer.classList.add('open');
  });

  keyHandler = (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if (state.getCommenting()) {
      state.setCommenting(false);
      updateComposer();
      return;
    }
    if (isMinimized) {
      setMinimized(false);
      return;
    }
    handleClose();
  };
  document.addEventListener('keydown', keyHandler);

  return drawer;
}

/**
 * Open the AI Assistant drawer
 */
export function openAIAssistant() {
  const existing = document.getElementById('ai-assistant-drawer');
  if (existing) {
    if (existing.classList.contains('ai-assistant-drawer--minimized')) {
      existing.querySelector('.ai-drawer-minimize-tab')?.click();
    }
    return;
  }

  const drawer = createAIAssistantDrawer({
    onClose: () => {},
  });

  document.body.appendChild(drawer);
}
